import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HelpersService } from '../../../../src/app/core/services/helpers.service';
import { NetworkCallService } from '../../../../src/app/core/services/network-call.service';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { retryTransientHttpErrors } from '../../core/operators/retry-transient-http-error.operator';
import * as staticDataCountries from '../actions/static-data-countries.actions';
import * as staticDataBusinessGroupsAction from '../actions/business-groups.actions';
import * as staticDataProductSolutionsAction from '../actions/static-data-product-solutions.actions';
import * as staticDataCountriesForSettings from '../actions/static-data-countries-for-settings.actions';
import * as staticDataIndividuals from '../actions/individuals.actions';
import { IStaticDataCountriesResponse } from '../models/static-data-countries.model';
import { IUserRolesResponse } from '../models/user-roles.model';
import {
  fromStaticDataUserRolesActions,
  fromStaticDataLegalEntityUserRolesActions,
  fromStaticDataAllUserRolesActions,
  fromStaticDataBusinessNamesActions,
  fromStaticDataProductNamesAction,
  fromRejectionReasonAction,
  fromStaticDataBanksAction,
  fromUploadedFileTypesAction,
  fromStaticDataAVCFrequenciesAction,
  fromProductTypesAction,
  fromProductSubTypesAction,
  fromProductCurrenciesAction,
  fromProductOnboardingFormsAction,
  fromProductStartingAgesAction,
  fromProductRetirementAgesAction,
  fromProductSwitchLockPeriodsAction,
  fromLockPeriodsAction,
  fromProductAnnualSwitchLimitsAction,
  fromProductProvidersAction,
  fromProductGoalsAction,
  fromNotificationTypesAction,
  fromStaticDataPreferredContactMethodsActions,
  fromStaticDataBusinessUserStatusActions,
  fromWithdrawalRejectionReasonAction
} from '../actions';
import { IProductsResponse } from '../models/products.model';
import { IBusinessNamesResponse } from '../models/business-names.model';
import { IRejectionReason } from '../models/rejection-reason.model';
import { IBanks } from '../models/banks.model';
import * as actions from '../actions';
import * as fromToDoactions from '../../features/todos/actions';
import { ILegalEntityTypesResponse } from '../models/legal-entity-types.model';
import { IAsqEmployerItem } from '../models/asq-employers.model';
import { IAsqFundItem } from '../models/asq-funds.model';
import { IAsqPortfolioItem } from '../models/asq-portfolios.model';
import { IBulkImportTemplateTypes } from '../models/bulk-import-template-types.model';
import { ILegalEntitiesCreateBusinessRequest } from '../../../../src/app/features/todos/models/create-business.model';
import { ITaxCertificateType } from '../models/tax-certificate-type.model';
import { fromOpenDocActions } from '../../features/employee-detail/actions';
import { IHttpRequestOptions } from '../../../../src/app/shared/interfaces/request-options';
import { IBusinessGroupNamesResponse } from '../../../../src/app/shared/models/shared.model';
import * as fromSharedActions from '../../../../src/app/shared/actions/shared.actions';
import { ResponseContentType } from '../../../../src/app/shared/enums';
import { IInviteStatus } from '../models/invite-status.model';
import { IUploadedFileTypesResponse } from '../models/uploaded-file-types.model';
import { Store } from '@ngrx/store';
import * as fromRefreshTokenActions from '../../../../src/app/features/auth/actions/refresh-token.actions';
import { IAVCFrequencyResponse } from '../models/avc-frequency.model';
import { INotificationTypesResponse } from '../models/notification-types.model';
import { IIndividualsResponse } from '../models/individuals.model';
import { HttpParams } from '@angular/common/http';
import { IStaticDataNumberResponse, IStaticDataResponse } from '../models/static-data.model';

/** Map file extension or imageType to MIME type so blobs open correctly (e.g. images in browser). */
function getMimeTypeFromDocumentName(documentName: string, imageType?: string): string {
  const extMap: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    bmp: 'image/bmp',
    tiff: 'image/tiff',
    tif: 'image/tiff',
    svg: 'image/svg+xml',
    xml: 'text/xml',
    rtf: 'text/rtf',
    txt: 'text/plain',
    mp4: 'video/mp4',
    webm: 'video/webm',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  };
  if (imageType) {
    const normalized = imageType.toLowerCase().replace(/^\./, '');
    if (extMap[normalized]) return extMap[normalized];
  }
  if (documentName) {
    const match = documentName.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    if (match) {
      const ext = match[1].toLowerCase();
      if (extMap[ext]) return extMap[ext];
    }
  }
  return 'application/octet-stream';
}

@Injectable()
export class StaticDataCountriesEffects {
  constructor(
    private actions$: Actions,
    private networkCallService: NetworkCallService,
    private store: Store,
    private helpers: HelpersService
  ) { }

  staticDataCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(staticDataCountries.loadStaticDataCountries),
      switchMap(action => {
        var params = new HttpParams();
        params = params.append('noLoader', action.noLoader);
        return this.networkCallService.get<IStaticDataCountriesResponse[]>('StaticData/country', { params }).pipe(
          map(response => {
            // mutate data before storing it in the immutable Store
            const data = [...response];
            data.map(countries => {
              const lowered = countries.description.toLowerCase();
              countries.description = lowered;
            });

            return staticDataCountries.loadStaticDataCountriesSuccess({
              response: data
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(staticDataCountries.loadStaticDataCountriesFailure(error));
          })
        );
      }
      )
    )
  );

  staticDataCountriesForSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(staticDataCountriesForSettings.loadStaticDataCountriesForSettings),
      mergeMap(action =>
        this.networkCallService.get<IStaticDataCountriesResponse[]>('StaticData/countriesforsettings').pipe(
          map(response => {
            const data = [...response];

            return staticDataCountriesForSettings.loadStaticDataCountriesForSettingsSuccess({
              response: data
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            const errorMsg = error?.error?.Message || error?.error?.message || '';
            return of(staticDataCountriesForSettings.loadStaticDataCountriesForSettingsFailure(error));
          })
        )
      )
    )
  );

  staticDataUserRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromStaticDataUserRolesActions.loadUserRoles),
      switchMap(action =>
        this.networkCallService.get<IUserRolesResponse[]>('StaticData/userRoles').pipe(
          map(response => {
            return fromStaticDataUserRolesActions.loadUserRolesSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(fromStaticDataUserRolesActions.loadUserRolesFailure(error));
          })
        )
      )
    )
  );

  staticDataLegalEntityUserRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromStaticDataLegalEntityUserRolesActions.loadLegalEntityUserRoles),
      switchMap(action =>
        this.networkCallService.get<IUserRolesResponse[]>('StaticData/legalEntityUserRoles').pipe(
          map(response => {
            return fromStaticDataLegalEntityUserRolesActions.loadLegalEntityUserRolesSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(fromStaticDataLegalEntityUserRolesActions.loadLegalEntityUserRolesFailure(error));
          })
        )
      )
    )
  );

  staticDataAllRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromStaticDataAllUserRolesActions.loadAllRoles),
      switchMap(action =>
        this.networkCallService.get<IUserRolesResponse[]>('StaticData/allRoles').pipe(
          map(response => {
            return fromStaticDataAllUserRolesActions.loadAllRolesSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(fromStaticDataAllUserRolesActions.loadAllRolesFailure(error));
          })
        )
      )
    )
  );

  staticDataProductTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductTypesAction.loadProductTypes),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/productTypes').pipe(
          map(response => {
            return fromProductTypesAction.loadProductTypesSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductTypesAction.loadProductTypesFailure(error));
          })
        )
      )
    )
  );

  staticDataProductSubTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductSubTypesAction.loadProductSubTypes),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/productSubTypes').pipe(
          map(response => {
            return fromProductSubTypesAction.loadProductSubTypesSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductSubTypesAction.loadProductSubTypesFailure(error));
          })
        )
      )
    )
  );

  staticDataProductSubTypesByType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductSubTypesAction.loadProductSubTypesByType),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>(`StaticData/productSubTypesByType/${action.request.typeId}`).pipe(
          map(response => {
            return fromProductSubTypesAction.loadProductSubTypesByTypeSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductSubTypesAction.loadProductSubTypesByTypeFailure(error));
          })
        )
      )
    )
  );

  staticDataProductCurrencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductCurrenciesAction.loadProductCurrencies),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/productCurrencies').pipe(
          map(response => {
            return fromProductCurrenciesAction.loadProductCurrenciesSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductCurrenciesAction.loadProductCurrenciesFailure(error));
          })
        )
      )
    )
  );

  staticDataProductOnboardingForms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductOnboardingFormsAction.loadProductOnboardingForms),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/productOnboardingForms').pipe(
          map(response => {
            return fromProductOnboardingFormsAction.loadProductOnboardingFormsSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductOnboardingFormsAction.loadProductOnboardingFormsFailure(error));
          })
        )
      )
    )
  );

  staticDataProductStartingAges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductStartingAgesAction.loadProductStartingAges),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/startingAges').pipe(
          map(response => {
            return fromProductStartingAgesAction.loadProductStartingAgesSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductStartingAgesAction.loadProductStartingAgesFailure(error));
          })
        )
      )
    )
  );

  staticDataProductRetirementAges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductRetirementAgesAction.loadProductRetirementAges),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/retirementAges').pipe(
          map(response => {
            return fromProductRetirementAgesAction.loadProductRetirementAgesSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductRetirementAgesAction.loadProductRetirementAgesFailure(error));
          })
        )
      )
    )
  );

  staticDataProductSwitchLockPeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductSwitchLockPeriodsAction.loadProductSwitchLockPeriods),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/lockPeriods').pipe(
          map(response => {
            return fromProductSwitchLockPeriodsAction.loadProductSwitchLockPeriodsSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductSwitchLockPeriodsAction.loadProductSwitchLockPeriodsFailure(error));
          })
        )
      )
    )
  );

  staticDataLockPeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromLockPeriodsAction.loadLockPeriods),
      switchMap(() =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/lockPeriods').pipe(
          map(response =>
            fromLockPeriodsAction.loadLockPeriodsSuccess({
              response
            })
          ),
          catchError(error => of(fromLockPeriodsAction.loadLockPeriodsFailure(error)))
        )
      )
    )
  );

  staticDataProductAnnualSwitchLimits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductAnnualSwitchLimitsAction.loadProductAnnualSwitchLimits),
      switchMap(action =>
        this.networkCallService.get<IStaticDataNumberResponse[]>('StaticData/annualSwitchLimits').pipe(
          map(response => {
            return fromProductAnnualSwitchLimitsAction.loadProductAnnualSwitchLimitsSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductAnnualSwitchLimitsAction.loadProductAnnualSwitchLimitsFailure(error));
          })
        )
      )
    )
  );

  staticDataProductProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductProvidersAction.loadProductProviders),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/directDebitProviders').pipe(
          map(response => {
            return fromProductProvidersAction.loadProductProvidersSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductProvidersAction.loadProductProvidersFailure(error));
          })
        )
      )
    )
  );

  staticDataProductGoals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProductGoalsAction.loadProductGoals),
      switchMap(action =>
        this.networkCallService.get<IStaticDataResponse[]>('StaticData/productGoal').pipe(
          map(response => {
            return fromProductGoalsAction.loadProductGoalsSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromProductGoalsAction.loadProductGoalsFailure(error));
          })
        )
      )
    )
  );


  staticDataPreferredContactMethods$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromStaticDataPreferredContactMethodsActions.loadPreferredContactMethods),
      switchMap(action => {
        return this.networkCallService
          .post<any, any>(`StaticData/PreferredContactMethods`)
          .pipe(
            mergeMap((response: any) => {
              return [
                fromStaticDataPreferredContactMethodsActions.loadPreferredContactMethodsSuccess({
                  response: response
                })
              ];
            }),
            catchError(error => {

              return of(
                fromStaticDataPreferredContactMethodsActions.loadPreferredContactMethodsFailure({
                  error
                })
              );
            })
          );
      })
    );
  });


  staticDataBusinessUserStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromStaticDataBusinessUserStatusActions.loadBusinessUserStatuses),
      switchMap(action => {
        return this.networkCallService
          .post<any, any>(`StaticData/BusinessUserStatus`)
          .pipe(
            mergeMap((response: any) => {
              return [
                fromStaticDataBusinessUserStatusActions.loadBusinessUserStatusesSuccess({
                  response: response
                })
              ];
            }),
            catchError(error => {

              return of(
                fromStaticDataBusinessUserStatusActions.loadBusinessUserStatusesFailure({
                  error
                })
              );
            })
          );
      })
    );
  });


  staticDataNotificationTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNotificationTypesAction.loadNotificationTypes),
      switchMap(action =>
        this.networkCallService.get<INotificationTypesResponse[]>('StaticData/notificationTypes').pipe(
          map(response => {
            return fromNotificationTypesAction.loadNotificationTypesSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(fromNotificationTypesAction.loadNotificationTypesFailure(error));
          })
        )
      )
    )
  );

  staticDataInviteStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadInviteStatus),
      switchMap(action =>
        this.networkCallService.get<IInviteStatus[]>('StaticData/inviteStatus').pipe(
          map(response => {
            return actions.loadInviteStatusSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadInviteStatusFailure(error));
          })
        )
      )
    )
  );

  staticDataBusinessNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromStaticDataBusinessNamesActions.loadBusinessNames),
      switchMap(action =>
        this.networkCallService.get<IBusinessNamesResponse[]>('Business/Businesses').pipe(
          map(response => {
            return fromStaticDataBusinessNamesActions.loadBusinessNamesSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(fromStaticDataBusinessNamesActions.loadBusinessNamesFailure(error));
          })
        )
      )
    )
  );

  staticDataProductNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromStaticDataProductNamesAction.loadProducts),
      switchMap(action =>
        this.networkCallService.get<IProductsResponse[]>('Business/Products').pipe(
          map(response => {
            return fromStaticDataProductNamesAction.loadProductsSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(fromStaticDataProductNamesAction.loadProductsFailure(error));
          })
        )
      )
    )
  );

  staticDataRejectionReason$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromRejectionReasonAction.loadUploadedFileRejectionReasons),
      switchMap(action =>
        this.networkCallService.get<IRejectionReason[]>('StaticData/uploadedFileRejectReason').pipe(
          map(response => {
            return fromRejectionReasonAction.loadUploadedFileRejectionReasonsSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromRejectionReasonAction.loadUploadedFileRejectionReasonsFailure(error));
          })
        )
      )
    )
  );

  staticDataWithdrawalRejectionReason$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWithdrawalRejectionReasonAction.loadWithdrawalRejectionReasons),
      switchMap(action =>
        this.networkCallService.get<IRejectionReason[]>('StaticData/WithdrawalRejectReason').pipe(
          map(response => {
            return fromWithdrawalRejectionReasonAction.loadWithdrawalRejectionReasonsSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromWithdrawalRejectionReasonAction.loadWithdrawalRejectionReasonsFailure(error));
          })
        )
      )
    )
  );

  staticDataBanks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromStaticDataBanksAction.loadBanks),
      switchMap(action =>
        this.networkCallService.get<IBanks[]>(`StaticData/bank?countryId=${action.request}`).pipe(
          map(response => {
            return fromStaticDataBanksAction.loadBanksSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            const errorMsg = error?.error?.Message || error?.error?.message || '';
            return of(fromStaticDataBanksAction.loadBanksFailure(error));
          })
        )
      )
    )
  );

  getDocumentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadDocumentTypes),
      switchMap(action =>
        this.networkCallService.get<any>(`StaticData/ProofOfIdentityDocumentTypes?isMinor=${action.isMinor ?? false}`).pipe(
          map(response => {
            return actions.loadDocumentTypesSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadDocumentTypesFailure(error));
          })
        )
      )
    )
  );

  getLegalEntityTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadLegalEntityTypes),
      switchMap(action =>
        this.networkCallService.get<ILegalEntityTypesResponse[]>(`StaticData/legalEntityTypes`).pipe(
          map(response => {
            return actions.loadLegalEntityTypesSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadLegalEntityTypesFailure(error));
          })
        )
      )
    )
  );

  getAsqEmployers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadAsqEmployers),
      switchMap(() =>
        this.networkCallService.get<IAsqEmployerItem[]>(`StaticData/ASQ/Employers`).pipe(
          map(response => {
            const sorted = Array.isArray(response)
              ? [...response].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
              : [];
            return actions.loadAsqEmployersSuccess({ response: sorted });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadAsqEmployersFailure(error));
          })
        )
      )
    )
  );

  getAsqFunds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadAsqFunds),
      switchMap(() =>
        this.networkCallService.get<IAsqFundItem[]>(`StaticData/ASQ/Funds`).pipe(
          map(response => {
            const sorted = Array.isArray(response)
              ? [...response].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
              : [];
            return actions.loadAsqFundsSuccess({ response: sorted });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadAsqFundsFailure(error));
          })
        )
      )
    )
  );

  getAsqPortfolios$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadAsqPortfolios),
      switchMap(() =>
        this.networkCallService.get<IAsqPortfolioItem[]>(`StaticData/ASQ/Portfolios`).pipe(
          map(response => {
            const sorted = Array.isArray(response)
              ? [...response].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
              : [];
            return actions.loadAsqPortfoliosSuccess({ response: sorted });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadAsqPortfoliosFailure({ error }));
          })
        )
      )
    )
  );

  // FOR PF Settings
  getAVCFrequencies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromStaticDataAVCFrequenciesAction.loadAVCFrequencies),
      switchMap(action => {
        return this.networkCallService.get<IAVCFrequencyResponse[]>(`StaticData/AdditionalVoluntaryContributionsFrequency`).pipe(
          mergeMap((response: any) => {
            return [
              fromStaticDataAVCFrequenciesAction.loadAVCFrequenciesSuccess({
                response
              })
            ];
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(
              fromStaticDataAVCFrequenciesAction.loadAVCFrequenciesFailure({
                error
              })
            );
          })
        );
      })
    );
  });

  getBulkImportTemplateTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadBulkImportTemplateTypes),
      switchMap(action =>
        this.networkCallService.get<IBulkImportTemplateTypes[]>(`Business/${action.request}/BulkImportTemplateTypes`).pipe(
          map(response => {
            return actions.loadBulkImportTemplateTypesSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadBulkImportTemplateTypesFailure(error));
          })
        )
      )
    )
  );

  legalEntityCreateBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromToDoactions.loadCreateBusiness),
      switchMap(action =>
        this.networkCallService.post<ILegalEntitiesCreateBusinessRequest, string>('Business/BusinessName', action.request).pipe(
          mergeMap(response => {
            this.helpers.notifySuccess(`Legal entity created`, 300, 3000);

            return [
              fromToDoactions.loadCreateBusinessSuccess({
                response
              }),
              fromSharedActions.loadLegalEntities({ param: '', noLoader: true }),
            ];
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(fromToDoactions.loadCreateBusinessFailure(error));
          })
        )
      )
    )
  );

  getTaxCertificateTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadGetTaxCertTypes),
      switchMap(action =>
        this.networkCallService.get<ITaxCertificateType[]>('StaticData/taxCertificateTypes').pipe(
          map(response => {
            return actions.loadGetTaxCertTypesSuccess({
              response: response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadGetTaxCertTypesFailure(error));
          })
        )
      )
    )
  );

  // FOR MEMBER ON BOARD
  businessGroupNames$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromSharedActions.loadBusinessGroupNames),
      switchMap(action => {
        return this.networkCallService.get<IBusinessGroupNamesResponse[]>(`Business/${action.businessId}/BusinessGroup`).pipe(
          map(response => {
            return fromSharedActions.loadBusinessGroupNamesSuccess({
              response
            });
          }),
          catchError(error => {

            return of(
              fromSharedActions.loadBusinessGroupNamesFailure({
                error
              })
            );
          })
        );
      })
    );
  });

  getIndividuals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(staticDataIndividuals.getIndividualsRequest),
      switchMap(action =>
        this.networkCallService.get<IIndividualsResponse[]>(`Business/IndividualCustomers`).pipe(
          map(response => {
            return staticDataIndividuals.getIndividualsResponse({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(staticDataIndividuals.getIndividualsFailure(error));
          })
        )
      )
    )
  );

  openDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromOpenDocActions.loadGetUploadedDocs),
      switchMap(action => {
        const options: IHttpRequestOptions = {
          responseType: ResponseContentType.BLOB
        };
        return this.networkCallService
          .get<string>(`Business/${action.request.businessId}/Documents/${action.request.uploadedFileId}`, options)
          .pipe(
            map(response => {
              this.helpers.downloadFile(response);

              return fromOpenDocActions.loadGetUploadedDocsSuccess({
                response: true
              });
            }),
            catchError(error => {
              return of(
                fromOpenDocActions.loadGetUploadedDocsFailure({
                  error
                })
              );
            })
          );
      })
    );
  });

  staticDataUploadedFileType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUploadedFileTypesAction.loadUploadedFileTypes),
      switchMap(action =>
        this.networkCallService.get<IUploadedFileTypesResponse[]>('StaticData/uploadedFileType').pipe(
          map(response => {
            return fromUploadedFileTypesAction.loadUploadedFileTypesSuccess({
              response
            });
          }),
          catchError(error => {
            return of(fromUploadedFileTypesAction.loadUploadedFileTypesFailure(error));
          })
        )
      )
    )
  );

  downloadDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.loadDownloadFile),
      switchMap(action => {
        const options: IHttpRequestOptions = {
          responseType: ResponseContentType.BLOB
        };
        return this.networkCallService.get<string>(`File/${action.request.documentName}/download`, options).pipe(
          map(response => {
            const mimeType = getMimeTypeFromDocumentName(
              action.request.documentName,
              action.request.imageType
            );
            const newBlob = new Blob([response], { type: mimeType });
            const data = window.URL.createObjectURL(newBlob);
            window.open(data, '_blank');
            return actions.loadDownloadFileSuccess({
              response
            });
          }),
          catchError(error => {
            return of(
              actions.loadDownloadFileFailure({
                error
              })
            );
          })
        );
      })
    );
  });

  // FOR MEMBER ON BOARD
  staticDataBusinessGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(staticDataBusinessGroupsAction.loadStaticDataBusinessGroups),
      switchMap(action => {
        return this.networkCallService.get<IStaticDataResponse[]>(`StaticData/${action.businessId}/BusinessGroup`).pipe(
          map(response => {
            return staticDataBusinessGroupsAction.loadStaticDataBusinessGroupsSuccess({
              response
            });
          }),
          catchError(error => {

            return of(
              staticDataBusinessGroupsAction.loadStaticDataBusinessGroupsFailure({
                error
              })
            );
          })
        );
      })
    );
  });

  staticDataProductSolutions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(staticDataProductSolutionsAction.loadStaticDataProductSolutions),
      switchMap(action => {
        var params = new HttpParams();
        params = params.append('noLoader', action.noLoader);

        return this.networkCallService
          .post<any, any>(`StaticData/ProductSolutions`, action.request, { params: params })
          .pipe(
            mergeMap((response: any) => {
              return [
                staticDataProductSolutionsAction.loadStaticDataProductSolutionsSuccess({
                  response: response
                })
              ];
            }),
            catchError(error => {

              return of(
                staticDataProductSolutionsAction.loadStaticDataProductSolutionsFailure({
                  error
                })
              );
            })
          );
      })
    );
  });


}
